import { useEffect, useState } from 'react';

import {
  Input,
  Text,
  cn,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@kognitos/lattice';

const TOOLTIP_DELAY_MS = 200;

type TEditableTitleLevel = 'base' | 'small' | 'xs';
type TEditableTitleWeight = 'normal' | 'medium' | 'semibold';
type TEditableTitleHeadingLevel = 1 | 2 | 3;

// Font size + line height applied to BOTH the display text and the edit input
// so the field doesn't jump when toggling between the two.
const LEVEL_TYPOGRAPHY: Record<TEditableTitleLevel, string> = {
  base: 'text-base leading-6',
  small: 'text-sm leading-5',
  xs: 'text-xs leading-4',
};

// The input mirrors the display typography. `base` needs an explicit `md:`
// size: lattice `Input`'s base classes end in `md:text-sm`, and
// tailwind-merge won't let an unprefixed `text-*` displace a `md:`-prefixed
// one, so without it the field silently drops to 14px at >=768px. On `base`
// that `md:text-base` also keeps the field at 16px, below which iOS zooms the
// page on focus.
const LEVEL_INPUT_TYPOGRAPHY: Record<TEditableTitleLevel, string> = {
  base: 'text-base leading-6 md:text-base',
  small: 'text-sm leading-5',
  // Same `md:` reason as `base`: without it lattice's trailing `md:text-sm`
  // wins and a 12px field jumps to 14px at >=768px, which is exactly the
  // width shift the twin-sizing exists to avoid. iOS zoom does not apply —
  // this level is for secondary labels, not primary text entry.
  xs: 'text-xs leading-4 md:text-xs',
};

/** `level` is also forwarded to lattice `Text`, whose scale names differ from
 *  ours. The explicit class in LEVEL_TYPOGRAPHY does the sizing; this only
 *  stops `Text`'s own default from competing with it. */
const TEXT_LEVEL: Record<TEditableTitleLevel, 'base' | 'small' | 'xSmall'> = {
  base: 'base',
  small: 'small',
  xs: 'xSmall',
};

const WEIGHT_CLASS: Record<TEditableTitleWeight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
};

interface IEditableTitleProps {
  /** Currently committed title. */
  value: string;
  /**
   * Commit a new title. Only fires when the trimmed draft is non-empty and
   * different from `value`. May be async — a rejection reverts the field to
   * `value` so a failed rename doesn't leave stale text on screen.
   */
  onRename: (nextValue: string) => void | Promise<void>;
  /** Allow click-to-edit. Renders read-only text when false. */
  isEditable?: boolean;
  /** Disable editing while a rename is in flight. */
  isDisabled?: boolean;
  /** Enter edit mode on mount. */
  autoFocusEdit?: boolean;
  level?: TEditableTitleLevel;
  weight?: TEditableTitleWeight;
  /**
   * Expose the display text as a heading of this rank, so a title that is
   * visually the page's heading is also one in the accessibility tree.
   * Lattice `Text` only maps `level` to typography and its `as` prop does not
   * accept heading tags, hence the explicit ARIA role.
   */
  headingLevel?: TEditableTitleHeadingLevel;
  /**
   * Committed when the field is emptied, e.g. "Untitled chat". Omitted keeps
   * the previous value instead, which is right for a name the user cannot be
   * allowed to blank but wrong where an explicit placeholder is expected.
   */
  emptyFallback?: string;
  /** Width/spacing on the display text and edit cell (e.g. `max-w-*`, `flex-1`). */
  className?: string;
  /** A slightly larger field while editing, for a title shown at heading size. */
  isRoomyWhileEditing?: boolean;
  maxLength?: number;
  placeholder?: string;
  inputName?: string;
  inputId?: string;
  textTestId?: string;
}

/**
 * Inline click-to-edit title: shows text that becomes an input on click and
 * commits the rename on blur / Enter. An invisible text twin keeps the input
 * sized to the content so the layout doesn't shift when entering edit mode.
 */
export function EditableTitle({
  value,
  onRename,
  isEditable = true,
  isDisabled = false,
  autoFocusEdit = false,
  level = 'base',
  weight = 'medium',
  headingLevel,
  emptyFallback,
  className,
  maxLength,
  placeholder,
  inputName,
  inputId,
  textTestId,
  isRoomyWhileEditing = false,
}: IEditableTitleProps) {
  const [draft, setDraft] = useState(value);
  const [isEditing, setIsEditing] = useState(autoFocusEdit && isEditable);
  // Tracks an in-flight rename so the title stays non-interactive until it
  // settles — otherwise a fast blur-then-click could fire a second rename.
  const [isSaving, setIsSaving] = useState(false);

  // eslint-disable-next-line no-restricted-syntax -- reason: sync committed value to local draft
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const isLocked = isDisabled || isSaving;
  const displayValue = draft || value;
  const typography = LEVEL_TYPOGRAPHY[level];
  const textClassName = cn(
    'col-start-1 row-start-1 rounded-sm border border-transparent px-1',
    typography,
    WEIGHT_CLASS[weight],
    className,
  );

  const startEditing = () => {
    if (!isEditable || isLocked) return;
    setIsEditing(true);
  };

  const commit = () => {
    setIsEditing(false);
    const trimmed = draft.trim();
    const next = trimmed || emptyFallback?.trim() || '';
    if (!next || next === value) {
      setDraft(value);
      return;
    }
    setIsSaving(true);
    void Promise.resolve(onRename(next))
      .catch(() => setDraft(value))
      .finally(() => setIsSaving(false));
  };

  if (isEditable && isEditing) {
    return (
      <div className={cn('inline-grid items-center', className)}>
        <Text
          level={TEXT_LEVEL[level]}
          weight={weight}
          aria-hidden
          className={cn(
            textClassName,
            // The wrapper already carries `className`; a margin repeated on
            // the twin (e.g. a negative one that aligns the title) would
            // shrink the column it sizes and clip the input's text.
            'invisible m-0 whitespace-pre',
            isRoomyWhileEditing && 'px-2 py-1',
          )}
        >
          {displayValue || ' '}
        </Text>
        <Input
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              (event.target as HTMLInputElement).blur();
              event.preventDefault();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              setDraft(value);
              setIsEditing(false);
            }
          }}
          className={cn(
            'col-start-1 row-start-1 h-auto w-full min-w-0 rounded-sm px-1 py-0 shadow-none',
            // Matches the twin above, which sizes the field.
            isRoomyWhileEditing && 'px-2 py-1',
            LEVEL_INPUT_TYPOGRAPHY[level],
            WEIGHT_CLASS[weight],
          )}
          variant="default"
          name={inputName}
          id={inputId}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={isLocked}
          autoFocus
          // Collapse the input's intrinsic min-width (HTML default size=20)
          // so the invisible twin above drives the column width.
          size={1}
        />
      </div>
    );
  }

  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS} variant="primary">
      <TooltipTrigger asChild>
        <Text
          level={TEXT_LEVEL[level]}
          weight={weight}
          className={cn(textClassName, 'flex items-center', {
            'cursor-text hover:border-input hover:bg-secondary hover:shadow-xs':
              isEditable && !isLocked,
            'cursor-not-allowed opacity-50': isEditable && isLocked,
          })}
          data-testid={textTestId}
          role={headingLevel ? 'heading' : undefined}
          aria-level={headingLevel}
          onClick={isEditable && !isLocked ? startEditing : undefined}
        >
          <span className="min-w-0 truncate">{displayValue}</span>
        </Text>
      </TooltipTrigger>
      <TooltipContent className="max-w-[300px] break-words">
        {displayValue}
      </TooltipContent>
    </Tooltip>
  );
}
